/**@namespace pdfGenerator */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from 'jszip';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import { rlLogo } from '@/utils/print-password/rl-logo.js';
import { rlLingoLogo } from '@/utils/print-password/lingo-logo.js';
import globeIconPng from '@/utils/print-password/assets/student-card-globe.png';
import emailIconPng from '@/utils/print-password/assets/student-card-username.png';
import lockIconPng from '@/utils/print-password/assets/student-card-lock.png';
import stopIconPng from '@/utils/print-password/assets/student-card-stop.png';
//fonts
import "@/utils/print-password/fonts/Inter_18pt-Bold-normal.js";
import "@/utils/print-password/fonts/Inter_18pt-Italic-normal.js";
import "@/utils/print-password/fonts/Inter_18pt-Regular-normal.js";
import "@/utils/print-password/fonts/Inter_18pt-Thin-normal.js";
import "@/utils/print-password/fonts/Poppins-SemiBold-bold.js";

// Card layout constants (all values in mm)
const CARD = {
  PAD: 2.5,             // padding (left/right/top)
  BOT_PAD: 4,           // bottom padding
  HDR_H: 11,            // header height
  HDR_GAP: 2,           // gap between header and sections
  LBL_H: 2.5,           // label height allocation
  LBL_GAP: 2,           // gap between label and input
  IN_H: 6,              // input row height
  SEC_GAP: 2,           // gap after input before divider
  DIV_GAP: 1.5,         // gap after divider line
  ICON_BOX: 6,          // icon box size (square)
  ICON_SZ: 3,           // icon stroke size within box
  LOGO_W_LINGO: 20, LOGO_H_LINGO: 10,
  LOGO_W_RL: 27,    LOGO_H_RL: 7.5,
  QR_FOOTER_GAP: 3,     // gap between password section and QR footer
  QR_FOOTER_H: 22,      // QR footer panel height (non-Lingo only)
  QR_SIZE: 18,          // QR image size (square, fits inside footer)
};

// Base URL where the learner app is hosted (non-Lingo flow). Used to build the
// QR payload — see `buildQrPayload` below.
const NON_LINGO_LOGIN_URL = 'https://app.reflectivelearning.co/login';

// Placeholder string used in place of the real password in the QR URL. The
// existing magic-link flow in LoginView.vue requires `pw` to be truthy but
// never validates its value (auth is `GET /v1/user/:uid`, which issues a JWT
// from the userId alone). Putting the actual password in the URL would leak
// it via browser history, Referer headers, and access logs (OWASP §3.7.4),
// so we use a placeholder instead. We still skip the QR entirely if a card
// has no password — that signals a misconfigured student whose card is broken.
const QR_PW_PLACEHOLDER = 'qr';

/**
 * Build the QR payload — a fully-qualified login URL keyed on the student's
 * userId. Matches the existing "magic-link" flow at LoginView.vue
 * (?uid=&pw=), so a phone camera scanning the QR is logged in directly, and
 * the in-app scanner can decode this URL and reuse the same auth path.
 *
 * The QR's lifetime tracks the lifetime of the student's userId (i.e. as
 * long as the account exists), aligned with the existing magic-link
 * mechanism. The password is intentionally NOT in the URL — see
 * QR_PW_PLACEHOLDER above for the OWASP rationale.
 */
const buildQrPayload = (card) => {
  const uid = card?._id ? String(card._id) : '';
  const hasPassword = typeof card?.password === 'string' && card.password.length > 0;
  if (!uid || !hasPassword) return '';
  const params = new URLSearchParams({ uid, pw: QR_PW_PLACEHOLDER });
  return `${NON_LINGO_LOGIN_URL}?${params.toString()}`;
};

const calcCardHeight = (doc, card, cardWidth, lingoMode) => {
  const txtW = cardWidth - CARD.PAD * 2 - CARD.ICON_BOX - 1 - 4;
  doc.setFont('Poppins-SemiBold', 'bold');
  doc.setFontSize(7.5);
  const email = typeof card.email === 'string' ? card.email : '';
  const lines = doc.splitTextToSize(email, txtW);
  const emailInH = CARD.IN_H + (lines.length - 1) * 3.5;
  const qrFooterH = lingoMode ? 0 : CARD.QR_FOOTER_GAP + CARD.QR_FOOTER_H;
  return (
    CARD.PAD + CARD.HDR_H + CARD.HDR_GAP +
    CARD.LBL_H + CARD.LBL_GAP + CARD.IN_H +
    CARD.SEC_GAP + 0.2 + CARD.DIV_GAP +
    CARD.LBL_H + CARD.LBL_GAP + emailInH +
    CARD.SEC_GAP + 0.2 + CARD.DIV_GAP +
    CARD.LBL_H + CARD.LBL_GAP + CARD.IN_H +
    qrFooterH +
    CARD.BOT_PAD
  );
};

const drawStudentCard = (doc, card, x, y, cardWidth, lingoMode, qrDataUrl) => {
  const iW = cardWidth - CARD.PAD * 2;
  const iBoxX = x + CARD.PAD;
  const logoX = lingoMode ? x + 2 : x + 4;   // RL logo gets a little extra left margin
  const txtX = x + CARD.PAD + CARD.ICON_BOX + 1;
  const txtW = iW - CARD.ICON_BOX - 1;

  doc.setFont('Poppins-SemiBold', 'bold');
  doc.setFontSize(7.5);
  const email = typeof card.email === 'string' ? card.email : '';
  const emailLines = doc.splitTextToSize(email, txtW - 4);
  const emailInH = CARD.IN_H + (emailLines.length - 1) * 3.5;

  const hasQrFooter = !lingoMode && !!qrDataUrl;
  const qrFooterH = hasQrFooter ? CARD.QR_FOOTER_GAP + CARD.QR_FOOTER_H : 0;
  const cardH = (
    CARD.PAD + CARD.HDR_H + CARD.HDR_GAP +
    CARD.LBL_H + CARD.LBL_GAP + CARD.IN_H +
    CARD.SEC_GAP + 0.2 + CARD.DIV_GAP +
    CARD.LBL_H + CARD.LBL_GAP + emailInH +
    CARD.SEC_GAP + 0.2 + CARD.DIV_GAP +
    CARD.LBL_H + CARD.LBL_GAP + CARD.IN_H +
    qrFooterH +
    CARD.BOT_PAD
  );

  // Card border
  doc.setDrawColor('#475467');
  doc.setLineWidth(0.25);
  doc.rect(x, y, cardWidth, cardH, 'S');

  let cY = y + CARD.PAD;

  // --- Header: logo + student name ---
  if (lingoMode) {
    doc.addImage(rlLingoLogo, 'PNG', logoX, cY - 0.8, CARD.LOGO_W_LINGO, CARD.LOGO_H_LINGO);
  } else {
    doc.addImage(rlLogo, 'PNG', logoX - 0.8, cY + 1.6, CARD.LOGO_W_RL, CARD.LOGO_H_RL);
  }
  const safeFirst = typeof card.firstName === 'string' ? card.firstName : '';
  const safeLast = typeof card.lastName === 'string' ? card.lastName : '';
  const fullName = (safeFirst + ' ' + safeLast).trim();
  doc.setFont('Inter_18pt-Bold', 'normal');
  doc.setFontSize(9);
  doc.setTextColor('#101828');
  const nW = doc.getTextWidth(fullName);
  doc.text(fullName, x + cardWidth - CARD.PAD - nW, cY + CARD.HDR_H / 2 + 1);

  cY += CARD.HDR_H + CARD.HDR_GAP;

  // --- URL section ---
  const urlLblY = cY + CARD.LBL_H;
  doc.setFontSize(7);
  doc.setTextColor('#344054');
  doc.setFont('Inter_18pt-Regular', 'normal');
  const u1 = 'Type this '; const u1W = doc.getTextWidth(u1);
  doc.text(u1, iBoxX, urlLblY);
  doc.setFont('Inter_18pt-Bold', 'normal');
  const u2 = 'at the very top'; const u2W = doc.getTextWidth(u2);
  doc.text(u2, iBoxX + u1W, urlLblY);
  doc.setFont('Inter_18pt-Regular', 'normal');
  doc.text(':', iBoxX + u1W + u2W, urlLblY);

  cY += CARD.LBL_H + CARD.LBL_GAP;

  // Globe icon (PNG)
  doc.setFillColor('#f4f2fe');
  doc.setDrawColor('#f4f2fe');
  doc.roundedRect(iBoxX, cY, CARD.ICON_BOX, CARD.ICON_BOX, 0.8, 0.8, 'F');
  const globeSz = 3.5;
  const globeOff = (CARD.ICON_BOX - globeSz) / 2;
  doc.addImage(globeIconPng, 'PNG', iBoxX + globeOff, cY + globeOff, globeSz, globeSz);

  doc.setDrawColor('#d0d5dd');
  doc.setLineWidth(0.2);
  doc.roundedRect(txtX, cY, txtW, CARD.IN_H, 0.8, 0.8, 'S');
  const siteUrl = lingoMode ? 'learn.lingo.school' : 'app.reflectivelearning.co';
  doc.setFont('Poppins-SemiBold', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor('#290e6e');
  doc.text(siteUrl, txtX + 2, cY + 4.0);

  cY += CARD.IN_H + CARD.SEC_GAP;

  // Divider 1
  doc.setDrawColor('#d0d5dd');
  doc.setLineWidth(0.2);
  doc.line(x + CARD.PAD, cY, x + cardWidth - CARD.PAD, cY);
  cY += 0.2 + CARD.DIV_GAP;

  // --- Username section ---
  const stopSz = 2.5;
  doc.addImage(stopIconPng, 'PNG', iBoxX, cY + 0.3, stopSz, stopSz);
  const lblTxtX = iBoxX + stopSz + 0.8;
  const usrLblY = cY + CARD.LBL_H;
  doc.setFontSize(7);
  doc.setFont('Inter_18pt-Bold', 'normal');
  doc.setTextColor('#c12930');
  const dn = 'DO NOT'; const dnW = doc.getTextWidth(dn);
  doc.text(dn, lblTxtX, usrLblY);
  doc.setFont('Inter_18pt-Regular', 'normal');
  doc.setTextColor('#344054');
  const midTxt = ' type your own email. Use this username '; const midW = doc.getTextWidth(midTxt);
  doc.text(midTxt, lblTxtX + dnW, usrLblY);
  doc.setFont('Inter_18pt-Bold', 'normal');
  const ex = 'EXACTLY'; const exW = doc.getTextWidth(ex);
  doc.text(ex, lblTxtX + dnW + midW, usrLblY);
  doc.setFont('Inter_18pt-Regular', 'normal');
  doc.text(':', lblTxtX + dnW + midW + exW, usrLblY);

  cY += CARD.LBL_H + CARD.LBL_GAP;

  doc.setFont('Poppins-SemiBold', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor('#290e6e');
  const emailImgSz = 3.5;
  if (emailLines.length > 1) {
    // Long email: icon on left, tall bordered input on right
    doc.setFillColor('#f4f2fe');
    doc.setDrawColor('#f4f2fe');
    doc.roundedRect(iBoxX, cY, CARD.ICON_BOX, emailInH, 0.8, 0.8, 'F');
    doc.addImage(emailIconPng, 'PNG',
      iBoxX + (CARD.ICON_BOX - emailImgSz) / 2,
      cY + (emailInH - emailImgSz) / 2,
      emailImgSz, emailImgSz);
    doc.setDrawColor('#d0d5dd');
    doc.setLineWidth(0.2);
    doc.roundedRect(txtX, cY, txtW, emailInH, 0.8, 0.8, 'S');
    doc.text(emailLines, txtX + 2, cY + 4.5, { lineHeightFactor: 1.3 });
  } else {
    // Short email: icon box + text input
    doc.setFillColor('#f4f2fe');
    doc.setDrawColor('#f4f2fe');
    doc.roundedRect(iBoxX, cY, CARD.ICON_BOX, CARD.ICON_BOX, 0.8, 0.8, 'F');
    doc.addImage(emailIconPng, 'PNG',
      iBoxX + (CARD.ICON_BOX - emailImgSz) / 2,
      cY + (CARD.ICON_BOX - emailImgSz) / 2,
      emailImgSz, emailImgSz);
    doc.setDrawColor('#d0d5dd');
    doc.setLineWidth(0.2);
    doc.roundedRect(txtX, cY, txtW, CARD.IN_H, 0.8, 0.8, 'S');
    doc.text(email, txtX + 2, cY + 4.0);
  }
  cY += emailInH + CARD.SEC_GAP;

  // Divider 2
  doc.setDrawColor('#d0d5dd');
  doc.setLineWidth(0.2);
  doc.line(x + CARD.PAD, cY, x + cardWidth - CARD.PAD, cY);
  cY += 0.2 + CARD.DIV_GAP;

  // --- Password section ---
  doc.setFontSize(7);
  doc.setTextColor('#344054');
  doc.setFont('Inter_18pt-Regular', 'normal');
  doc.text('Enter this password', iBoxX, cY + CARD.LBL_H);

  cY += CARD.LBL_H + CARD.LBL_GAP;

  // Lock icon (PNG)
  doc.setFillColor('#f4f2fe');
  doc.setDrawColor('#f4f2fe');
  doc.roundedRect(iBoxX, cY, CARD.ICON_BOX, CARD.ICON_BOX, 0.8, 0.8, 'F');
  const lockSz = 3.5;
  doc.addImage(lockIconPng, 'PNG',
    iBoxX + (CARD.ICON_BOX - lockSz) / 2,
    cY + (CARD.ICON_BOX - lockSz) / 2,
    lockSz, lockSz);

  doc.setDrawColor('#d0d5dd');
  doc.setLineWidth(0.2);
  doc.roundedRect(txtX, cY, txtW, CARD.IN_H, 0.8, 0.8, 'S');
  const pwd = typeof card.password === 'string' ? card.password : '';
  doc.setFont('Poppins-SemiBold', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor('#290e6e');
  doc.text(pwd, txtX + 2, cY + 4.0);

  // --- QR footer (non-Lingo only) ---
  if (hasQrFooter) {
    cY += CARD.IN_H + CARD.QR_FOOTER_GAP;

    // Gray footer panel spanning the full card width
    doc.setFillColor('#f2f4f7');
    doc.rect(x, cY, cardWidth, CARD.QR_FOOTER_H, 'F');

    // QR image (right-aligned within the footer)
    const qrPad = (CARD.QR_FOOTER_H - CARD.QR_SIZE) / 2;
    const qrX = x + cardWidth - CARD.PAD - CARD.QR_SIZE;
    const qrY = cY + qrPad;
    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, CARD.QR_SIZE, CARD.QR_SIZE);

    // Footer text — "Scan the QR code to login to your account."
    // "login" is rendered in the brand purple to match the Figma design.
    doc.setFont('Inter_18pt-Regular', 'normal');
    doc.setFontSize(7.5);
    const textBaselineY = cY + CARD.QR_FOOTER_H / 2 + 1;
    const p1 = 'Scan the QR code to ';
    const p2 = 'login';
    const p3 = ' to your account.';
    const p1W = doc.getTextWidth(p1);
    doc.setFont('Inter_18pt-Bold', 'normal');
    const p2W = doc.getTextWidth(p2);
    doc.setFont('Inter_18pt-Regular', 'normal');
    const p3W = doc.getTextWidth(p3);
    const totalW = p1W + p2W + p3W;
    // Centre the text block within the available space left of the QR
    const availableW = qrX - x - CARD.PAD;
    const textStartX = x + CARD.PAD + Math.max(0, (availableW - totalW) / 2);
    doc.setTextColor('#344054');
    doc.text(p1, textStartX, textBaselineY);
    doc.setFont('Inter_18pt-Bold', 'normal');
    doc.setTextColor('#602de6');
    doc.text(p2, textStartX + p1W, textBaselineY);
    doc.setFont('Inter_18pt-Regular', 'normal');
    doc.setTextColor('#344054');
    doc.text(p3, textStartX + p1W + p2W, textBaselineY);
  }

  return cardH;
};

const generateQrDataUrl = async (payload) => {
  if (!payload) return null;
  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: 'M',
    margin: 1,
    scale: 8,
    color: { dark: '#101828', light: '#f2f4f7' }
  });
};

/**
 * Generate a PDF with class details
 * @param {Object} classDetails - Object containing class information
 * @param {string} classDetails.schoolName - Name of the school
 * @param {string} classDetails.gradeLevel - Grade level of the class
 * @param {string} classDetails.name - Name of the class
 * @param {Array} classDetails.students - Array of student objects
 * @param {Bool} downloadFile - Whether to download the file directly or return as ArrayBuffer
 * @param {Bool} lingoMode - Whether to use Lingo branding and login URL
 */
const generatePdf = async (classDetails, downloadFile = true, lingoMode = false) => {
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4'
  });
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;

  if (!classDetails?.students || classDetails.students.length === 0) {
    console.warn("No students found in class details. Skipping PDF generation.");
    return false;
  }

  // --- Pages 1+: Student login cards ---
  const cardWidth = 90;
  const margin = 3;
  const rowGap = 8;
  const columnSpacing = 10;
  const startX = margin + 5;
  const startY = margin + 5;
  const cards = [...classDetails.students];

  // Pre-generate QR data URLs for non-Lingo cards (QRCode.toDataURL is async,
  // jsPDF.addImage is sync, so we materialise them up-front).
  const qrByIndex = new Array(cards.length).fill(null);
  if (!lingoMode) {
    await Promise.all(cards.map(async (card, idx) => {
      const payload = buildQrPayload(card);
      if (payload) {
        try {
          qrByIndex[idx] = await generateQrDataUrl(payload);
        } catch (err) {
          // If a single QR fails to render we still want the rest of the
          // sheet to print — the printed credentials remain a working fallback.
          console.warn('Failed to generate QR for login card', err);
        }
      }
    }));
  }

  let y = startY;
  for (let i = 0; i < cards.length; i += 2) {
    const leftCard = cards[i];
    const rightCard = cards[i + 1];
    const leftH = calcCardHeight(doc, leftCard, cardWidth, lingoMode);
    const rightH = rightCard ? calcCardHeight(doc, rightCard, cardWidth, lingoMode) : 0;
    const rowH = Math.max(leftH, rightH);

    if (i > 0 && y + rowH > pageHeight - margin) {
      doc.addPage();
      y = startY;
    }

    drawStudentCard(doc, leftCard, startX, y, cardWidth, lingoMode, qrByIndex[i]);
    if (rightCard) {
      drawStudentCard(doc, rightCard, startX + cardWidth + columnSpacing, y, cardWidth, lingoMode, qrByIndex[i + 1]);
    }

    y += rowH + rowGap;
  }

  // --- New page: Class summary (header info + student table) ---
  doc.addPage();

  if (lingoMode === true) {
    doc.addImage(rlLingoLogo, 'PNG', 147, 12, 24, 12.31);
  } else {
    doc.addImage(rlLogo, 'PNG', 152, 10, 40, 11.24);
  }

  doc.setTextColor(0);
  doc.setFont("Inter_18pt-Regular", "normal");
  doc.setFontSize(10);
  if (lingoMode === true) {
    const sitePrefixText = "address: ";
    const siteText = "learn.lingo.school";
    const textX = 165;
    const textY = 28;
    doc.text(siteText, textX, textY);
    doc.text(sitePrefixText, textX - doc.getTextWidth(sitePrefixText) - 0.5, textY);
    const textWidth = doc.getTextWidth(siteText);
    doc.setLineWidth(0.2);
    doc.setDrawColor(0, 0, 0);
    doc.line(textX, textY + 1.0, textX + textWidth, textY + 1.0);
  } else {
    doc.text("app.reflectivelearning.co", 152, 25);
  }

  // School name — wraps at 60% of page width so it doesn't collide with the logo
  const schoolName = classDetails.schoolName || "Unknown School";
  const maxSchoolNameWidth = pageWidth * 0.6;
  doc.setFont("Inter_18pt-Bold", "normal");
  doc.setFontSize(18);
  const schoolNameLines = doc.splitTextToSize(schoolName, maxSchoolNameWidth);
  doc.text(schoolNameLines, 15, 18);
  // 18pt × 0.352778mm/pt × 1.15 line-height ≈ 7.3mm per extra line
  const schoolNameLineH = 18 * 0.352778 * 1.15;
  const extraSchoolNameH = (schoolNameLines.length - 1) * schoolNameLineH;

  doc.setFontSize(12);
  doc.text(classDetails.subject, 15, 25 + extraSchoolNameH);
  doc.text("Grade: " + classDetails.gradeLevel, 15, 31 + extraSchoolNameH);
  doc.text("Class: " + classDetails.name, 15, 37 + extraSchoolNameH);

  const studentData = classDetails.students.map(student => [
    student.firstName.slice(0, 20) + " " + student.lastName.slice(0, 20),
    student.email,
    student.password
  ]);

  autoTable(doc, {
    head: [['Name', 'Email', 'Password']],
    body: [
      ...studentData,
    ],
    startY: 42 + extraSchoolNameH,
    styles: {
      tableWidth: 'wrap',
      overflow: 'linebreak',
      cellWidth: 'wrap',
      fillColour: ['37, 99, 235']
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 75 },
      2: { cellWidth: 46 }
    }
  });

  // Download the file or return as ArrayBuffer for zipping
  if (downloadFile === true) {
    const fileName = "Print Logins for " + classDetails.schoolName + " - " + classDetails.name + ".pdf";
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    return await blob.arrayBuffer(); // return as ArrayBuffer for zip
  }
};

/**
 * Generate ZIP of PDFs for multiple classes
 * @param {Array} classGroups - Array of class objects [{ name, learners }]
 */
const generateZip = async function generateZip(classes, lingoMode = false) {
  const zip = new JSZip();

  for (const classGroup of classes) {
    const pdfData = await generatePdf(classGroup, false, lingoMode);
    if (pdfData !== false) {
      const fileName = classGroup.schoolName + " - " + classGroup.name + " Logins.pdf";
      zip.file(fileName, pdfData);
    }
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, 'Print Logins - All Classes.zip');
}

export {
  generatePdf,
  generateZip
}
